import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    SxProps,
    Theme,
    Box,
    Grid,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    ListItemIcon,
    Collapse,
    TextField,
} from "@mui/material";
import SearchButton from "./SearchButton";
import SearchIcon from "./icons/SearchIcon";
import ArrowClosedIcon from "./icons/ArrowClosedIcon";
import ArrowOpenedIcon from "./icons/ArrowOpenedIcon";
import { isMobile } from "react-device-detect";
export enum FaqTreeType {
    Software,
    Testing,
}

interface FaqTreeItem {
    title: string;
    content?: string;
}

interface Props {
    type: FaqTreeType;
    tree: FaqTreeItem[];
    onSearchForStandard?: () => void;
}

const FaqTreeStyle: Record<string, SxProps<Theme>> = {
    container: {
        flexGrow: 1,
        textAlign: "left",
        height: "75vh",
    },
    containerMobile: {
        flexGrow: 1,
        textAlign: "left",
        height: "60vh",
    },
    content: {
        padding: "40px",
        border: "1px solid white",
        borderRadius: "20px",
        height: "100%",
        overflow: "hidden",
    },
    faqIcon: {
        minWidth: 0,
        marginRight: "10px",
        justifyContent: "center",
        alignItems: "center",
        svg: {
            marginTop: "8px",
        },
        img: {
            width: "20px",
            height: "auto",
        },
    },
    faqItemTitle: {
        fontSize: "22px",
    },
    faqItemTitleMobile: {
        fontSize: "18px",
    },
    faqContent: {
        paddingLeft: "30px",
        paddingRight: "30px",
        paddingTop: "15px",
    },
    faqContentText: {
        fontSize: "20px",
    },
    faqContentTextMobile: {
        fontSize: "17px",
    },
};

const FaqTree: React.FC<Props> = ({ tree, type, onSearchForStandard }) => {
    let navigate = useNavigate();

    const [shown, setShown] = useState<number[]>([]);
    const [textFilter, setTextFilter] = useState("");

    const handleShowItem = (idx: number) => {
        if (shown.includes(idx)) {
            setShown(shown.filter((_id) => _id !== idx));
        } else {
            setShown([...shown, idx]);
        }
    };

    const filterItems = (): FaqTreeItem[] => {
        return tree.filter(
            (_item) =>
                (_item.content && _item.content.toLowerCase().includes(textFilter.toLowerCase())) ||
                _item.title.toLowerCase().includes(textFilter.toLowerCase()) ||
                _item.title === "Separator"
        );
    };

    return (
        <Box sx={isMobile ? FaqTreeStyle.containerMobile : FaqTreeStyle.container}>
            {isMobile && (
                <Grid
                    container
                    md={12}
                    sx={{ marginBottom: type === FaqTreeType.Software ? "20px" : "0px" }}
                >
                    <Grid item md={4} sm={6} xs={type === FaqTreeType.Software ? 12 : 7}>
                        <SearchIcon color="secondary" sx={{ mr: 1, my: 0.5 }} />
                        <TextField
                            value={textFilter}
                            onChange={(e) => setTextFilter(e.target.value)}
                            id="input-search"
                            variant="standard"
                            sx={{
                                width: isMobile
                                    ? type === FaqTreeType.Software
                                        ? "80%"
                                        : "60%"
                                    : "80%",
                                "& .MuiInput-underline:before": {
                                    borderBottomColor: "white",
                                },
                                "& .MuiInput-underline:after": {
                                    borderBottomColor:
                                        type === FaqTreeType.Software
                                            ? "software.main"
                                            : "testing.main",
                                },
                            }}
                            inputProps={{
                                style: {
                                    color: "white",
                                },
                            }}
                            InputLabelProps={{
                                shrink: true,
                                sx: {
                                    "&.MuiInputLabel-formControl": {
                                        color: "text.primary",
                                    },
                                    "&.MuiInputLabel-shrink": {
                                        color: "text.primary",
                                    },
                                    "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: "text.pr",
                                            borderWidth: "3px",
                                        },
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item md={8} sm={6} xs={4}>
                        {type === FaqTreeType.Testing && (
                            <SearchButton
                                color={"testing.main"}
                                style={{ marginRight: "20px" }}
                                onClick={() => {
                                    onSearchForStandard!();
                                }}
                            >
                                Search For Standard
                            </SearchButton>
                        )}
                        {!isMobile && (
                            <SearchButton
                                color={
                                    type === FaqTreeType.Software
                                        ? "software.main"
                                        : "testing.main"
                                }
                                onClick={() => {
                                    if (type === FaqTreeType.Software) {
                                        navigate("/testing");
                                    } else {
                                        navigate("/software");
                                    }
                                }}
                            >
                                {type === FaqTreeType.Software ? "TIC - Testing, Inspection & Certification" : "Connective Engineering"}
                            </SearchButton>
                        )}
                    </Grid>
                </Grid>
            )}

            <Box
                sx={[
                    FaqTreeStyle.content,
                    isMobile ? { padding: "10px", paddingTop: "30px" } : {},
                ].flat(2)}
            >
                <Box>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {!isMobile && (
                            <Grid container md={12}>
                                <Grid
                                    item
                                    md={4}
                                    sm={6}
                                    xs={type === FaqTreeType.Software ? 12 : 7}
                                >
                                    <SearchIcon color="secondary" sx={{ mr: 1, my: 0.5 }} />
                                    <TextField
                                        value={textFilter}
                                        onChange={(e) => setTextFilter(e.target.value)}
                                        id="input-search"
                                        variant="standard"
                                        sx={{
                                            width: isMobile
                                                ? type === FaqTreeType.Software
                                                    ? "80%"
                                                    : "60%"
                                                : "80%",
                                            "& .MuiInput-underline:before": {
                                                borderBottomColor: "white",
                                            },
                                            "& .MuiInput-underline:after": {
                                                borderBottomColor:
                                                    type === FaqTreeType.Software
                                                        ? "software.main"
                                                        : "testing.main",
                                            },
                                        }}
                                        inputProps={{
                                            style: {
                                                color: "white",
                                            },
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                            sx: {
                                                "&.MuiInputLabel-formControl": {
                                                    color: "text.primary",
                                                },
                                                "&.MuiInputLabel-shrink": {
                                                    color: "text.primary",
                                                },
                                                "&.Mui-focused": {
                                                    "& .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: "text.pr",
                                                        borderWidth: "3px",
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                </Grid>

                                <Grid item md={8} sm={6} xs={4}>
                                    {type === FaqTreeType.Testing && (
                                        <SearchButton
                                            color={"testing.main"}
                                            style={{ marginRight: "20px" }}
                                            onClick={() => {
                                                onSearchForStandard!();
                                            }}
                                        >
                                            Search For Standard
                                        </SearchButton>
                                    )}
                                    {!isMobile && (
                                        <SearchButton
                                            color={
                                                type === FaqTreeType.Software
                                                    ? "software.main"
                                                    : "testing.main"
                                            }
                                            onClick={() => {
                                                if (type === FaqTreeType.Software) {
                                                    navigate("/testing");
                                                } else {
                                                    navigate("/software");
                                                }
                                            }}
                                        >
                                            {type === FaqTreeType.Software ? "TIC - Testing, Inspection & Certification" : "Connective Engineering"}
                                        </SearchButton>
                                    )}
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        height: "90%",
                        overflow: "auto",
                        "&::-webkit-scrollbar": {
                            borderRadius: 2,
                            width: "0.5em",
                            border: "1px solid white",
                        },
                        "&::-webkit-scrollbar-track": {
                            background: "#81BC0047",
                            borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            background: "#81BC00",
                            borderRadius: "5px",
                            border: "1px solid white",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                            background: "#81BC00",
                        },
                    }}
                >
                    <List>
                        {filterItems().map((item, idx) => {
                            if (item.title === "Separator") {
                                return <hr style={{ borderTop: "1px solid white", borderBottom: "none" }} />
                            }
                            else if (item.title === "Title") {
                                return <>
                                    <ListItemText
                                        primary={item.content}
                                        primaryTypographyProps={{
                                            sx: isMobile
                                                ? FaqTreeStyle.faqItemTitleMobile
                                                : FaqTreeStyle.faqItemTitle,
                                        }}
                                    />
                                </>
                            }
                            return (
                                <>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleShowItem(idx)}>
                                            <ListItemIcon sx={FaqTreeStyle.faqIcon}>
                                                {shown.includes(idx) ? (
                                                    <ArrowOpenedIcon
                                                        color="secondary"
                                                        sx={{
                                                            color:
                                                                type === FaqTreeType.Software
                                                                    ? "software.main"
                                                                    : "testing.main",
                                                        }}
                                                    />
                                                ) : (
                                                    <ArrowClosedIcon
                                                        sx={{
                                                            color:
                                                                type === FaqTreeType.Software
                                                                    ? "software.main"
                                                                    : "testing.main",
                                                        }}
                                                    />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.title}
                                                primaryTypographyProps={{
                                                    sx: isMobile
                                                        ? FaqTreeStyle.faqItemTitleMobile
                                                        : FaqTreeStyle.faqItemTitle,
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    <Collapse
                                        sx={FaqTreeStyle.faqContent}
                                        in={shown.includes(idx)}
                                        key={"a"}
                                    >
                                        <Typography
                                            sx={
                                                isMobile
                                                    ? FaqTreeStyle.faqContentTextMobile
                                                    : FaqTreeStyle.faqContentText
                                            }
                                        >
                                            {item.content}
                                        </Typography>
                                    </Collapse>
                                </>
                            )
                        }

                        )}
                    </List>
                </Box>
            </Box>
        </Box>
    );
};

export default FaqTree;
